import { plb, text, year } from './columnSpec.types';

import type { HoursColumnSpec } from './columnSpec.types';

/**
 * Ported from `EmployeeWorkAgreementsGridColumns.js:579-906`. Everything here sat inside
 * `if (plbFlags.indexOf('PLB_VISIBILITY') > -1)`, so every row carries `requiresFlag`.
 *
 * The `plbAge*` history columns are the `PLB_10_YEARS` split (`:623-669`): without the flag Dojo
 * showed `plbAge5` as "<year-6> and older"; with it, `plbAge5..plbAge10` each get their own year and
 * `plbAge10` becomes the "and older" bucket.
 *
 * Known deviation from Dojo: `plbDetailsChildren` listed `hoursPlbBalanceCum` TWICE in the
 * plbHistory group — once at `:582` and again at `:678` — with an identical label, so the same value
 * rendered twice in the same group. That is a copy-paste bug, not a feature; it appears once here.
 */
export const PLB_HOURS_COLUMNS: HoursColumnSpec[] = [
  // PLB — :688-709
  {
    id: 'hoursPlbRights',
    source: 'hoursPlbRights',
    group: 'plb',
    label: plb('hoursPlbRights'),
    tooltipKey: 'hoursPlbRights',
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbBalanceCum',
    source: 'hoursPlbBalanceCum',
    group: 'plb',
    label: plb('hoursPlbBalanceCum'),
    tooltipKey: 'hoursPlbBalanceCum',
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB details — :712-731
  {
    id: 'plbCorrection',
    source: 'plbCorrection',
    group: 'plbDetails',
    label: plb('allHoursPlbCorTotal'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlb',
    source: 'hoursPlb',
    group: 'plbDetails',
    label: plb('hoursPlb'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB hours distribution — :734-760
  {
    id: 'hoursPlbAssignedLv',
    source: 'hoursPlbAssignedLv',
    group: 'plbDistribution',
    label: plb('hoursPlbAssignedLv'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbAssignedWork',
    source: 'hoursPlbAssignedWork',
    group: 'plbDistribution',
    label: plb('hoursPlbAssignedWork'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbAssignedLcs',
    source: 'hoursPlbAssignedLcs',
    group: 'plbDistribution',
    label: plb('hoursPlbAssignedLcs'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB hours recording — :763-803
  {
    id: 'hoursPlbUsedLv',
    source: 'hoursPlbUsedLv',
    group: 'plbRecording',
    label: plb('hoursPlbUsedLv'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbUsedWork',
    source: 'hoursPlbUsedWork',
    group: 'plbRecording',
    label: plb('hoursPlbUsedWork'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbUsedLcs',
    source: 'hoursPlbUsedLcs',
    group: 'plbRecording',
    label: plb('hoursPlbUsedLcs'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbUsedDirect:plbRecording',
    source: 'hoursPlbUsedDirect',
    group: 'plbRecording',
    label: plb('hoursPlbUsedDirect'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbPaidTotal',
    source: 'hoursPlbPaidTotal',
    group: 'plbRecording',
    label: plb('hoursPlbPaidTotal'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB hours summary — :806-825
  {
    id: 'hoursPlbUsedTot',
    source: 'hoursPlbUsedTot',
    group: 'plbSummary',
    label: plb('hoursPlbUsedTot'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbBalance',
    source: 'hoursPlbBalance',
    group: 'plbSummary',
    label: plb('hoursPlbBalance'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // PLB history — :580-682, rendered as the plbHistory group at :827-831
  {
    id: 'hoursPlbBalanceCum:plbHistory',
    source: 'hoursPlbBalanceCum',
    group: 'plbHistory',
    label: plb('hoursPlbBalanceCum'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'plbCurr',
    source: 'plbCurr',
    group: 'plbHistory',
    label: year(0, 'plbCurr', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    // detail window only, promoted out of the correction form (`…Form.js:512-543`)
    id: 'plbOneTimeDepositRato',
    source: 'plbOneTimeDepositRato',
    group: 'plbHistory',
    label: plb('corrections.oneTimeAllowance'),
    requiresFlag: 'PLB_VISIBILITY',
    detailOnly: true
  },
  {
    id: 'plbAgePrev',
    source: 'plbAgePrev',
    group: 'plbHistory',
    label: year(1, 'plbAgePrev', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'plbAge1',
    source: 'plbAge1',
    group: 'plbHistory',
    label: year(2, 'plbAge1', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'plbAge2',
    source: 'plbAge2',
    group: 'plbHistory',
    label: year(3, 'plbAge2', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'plbAge3',
    source: 'plbAge3',
    group: 'plbHistory',
    label: year(4, 'plbAge3', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'plbAge4',
    source: 'plbAge4',
    group: 'plbHistory',
    label: year(5, 'plbAge4', { fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // plbAge5 without PLB_10_YEARS is the "and older" bucket (:663-668); with the flag it is a plain
  // year and plbAge10 becomes the bucket (:624-661).
  {
    id: 'plbAge5:andOlder',
    source: 'plbAge5',
    group: 'plbHistory',
    label: year(6, 'plbAge5', { andOlder: true, fallbackIsPlb: true }),
    requiresFlag: 'PLB_VISIBILITY',
    excludedByFlag: 'PLB_10_YEARS'
  },
  {
    id: 'plbAge5',
    source: 'plbAge5',
    group: 'plbHistory',
    label: year(6, 'plbAge5', { fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    id: 'plbAge6',
    source: 'plbAge6',
    group: 'plbHistory',
    label: year(7, 'plbAge6', { fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    id: 'plbAge7',
    source: 'plbAge7',
    group: 'plbHistory',
    label: year(8, 'plbAge7', { fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    id: 'plbAge8',
    source: 'plbAge8',
    group: 'plbHistory',
    label: year(9, 'plbAge8', { fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    id: 'plbAge9',
    source: 'plbAge9',
    group: 'plbHistory',
    label: year(10, 'plbAge9', { fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },
  {
    id: 'plbAge10',
    source: 'plbAge10',
    group: 'plbHistory',
    label: year(11, 'plbAge10', { andOlder: true, fallbackIsPlb: true }),
    requiresFlag: ['PLB_VISIBILITY', 'PLB_10_YEARS']
  },

  {
    id: 'hoursPlbRights:plbHistory',
    source: 'hoursPlbRights',
    group: 'plbHistory',
    label: plb('hoursPlbRights'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // LL structure — :834-853.
  // Deliberate deviation: Dojo reused the same two translation keys here WITHOUT the plbName
  // replacement it applied in the plbDistribution/plbRecording groups (:753 vs :839), so a tenant
  // with a custom budget name saw the literal "PLB" in these two headers only. Interpolated here for
  // consistency — the one place this port changes a header a user could notice.
  {
    id: 'hoursPlbAssignedLcs:llStructure',
    source: 'hoursPlbAssignedLcs',
    group: 'llStructure',
    label: plb('hoursPlbAssignedLcs'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursPlbUsedLcs:llStructure',
    source: 'hoursPlbUsedLcs',
    group: 'llStructure',
    label: plb('hoursPlbUsedLcs'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // LL deposit — :856-875
  {
    id: 'hoursLcsUsedFin',
    source: 'hoursLcsUsedFin',
    group: 'llDeposit',
    label: text('hoursLcsUsedFin'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursLcsUsedLife',
    source: 'hoursLcsUsedLife',
    group: 'llDeposit',
    label: text('hoursLcsUsedLife'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // LL details — :878-904
  {
    id: 'inputHoursLcs',
    source: 'inputHoursLcs',
    group: 'llDetails',
    label: text('inputHoursLcs'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursLcsBalance',
    source: 'hoursLcsBalance',
    group: 'llDetails',
    label: text('hoursLcsBalance'),
    requiresFlag: 'PLB_VISIBILITY'
  },
  {
    id: 'hoursLcsBalanceCum',
    source: 'hoursLcsBalanceCum',
    group: 'llDetails',
    label: text('hoursLcsBalanceCum'),
    requiresFlag: 'PLB_VISIBILITY'
  },

  // Balance budget — :981-1022 of the correction form. Detail window only, December only, and only
  // with the balance-budget grant; the values come from a separate overview request.
  {
    id: 'savedPlusHours',
    source: 'savedPlusHours',
    group: 'balanceBudget',
    label: plb('corrections.hourBalanceCorrection'),
    detailOnly: true,
    requiresBalanceBudget: true
  },
  {
    id: 'savedExtraStatutoryLeaveHours',
    source: 'savedExtraStatutoryLeaveHours',
    group: 'balanceBudget',
    label: plb('corrections.depositedLeave'),
    detailOnly: true,
    requiresBalanceBudget: true
  },
  {
    id: 'savedPLBHours',
    source: 'savedPLBHours',
    group: 'balanceBudget',
    label: plb('corrections.depositedPlb'),
    detailOnly: true,
    requiresBalanceBudget: true
  }
];
